<script setup>
import { computed, ref, shallowRef, watch } from 'vue'
import AuthIcon from './AuthIcon.vue'

const props = defineProps({
  id: { type: String, default: 'otp' },
  modelValue: { type: String, default: '' },
  error: { type: String, default: '' },
  length: { type: Number, default: 6 },
  legend: { type: String, default: 'Verification code' },
  autocomplete: { type: String, default: 'one-time-code' },
  concealed: { type: Boolean, default: false },
  visibilityToggle: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  profile: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue'])

const digits = ref(Array(props.length).fill(''))
const inputRefs = []
const isVisible = shallowRef(!props.concealed)
const inputType = computed(() => props.concealed && !isVisible.value ? 'password' : 'text')
const rootClasses = computed(() => ({
  'auth-login__otp-wrap--compact': props.compact,
  'auth-login__otp-wrap--with-visibility': props.compact && props.concealed && props.visibilityToggle,
  'auth-login__otp-wrap--profile': props.profile,
}))
const legendClass = computed(() => props.profile
  ? 'auth-login__otp-label--profile'
  : 'auth-login__sr-only')

watch(() => props.modelValue, (value) => {
  const characters = String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, props.length)
    .split('')

  digits.value = Array.from(
    { length: props.length },
    (_, index) => characters[index] ?? '',
  )
}, { immediate: true })

const setRef = (element, index) => {
  if (element) inputRefs[index] = element
}

const emitValue = () => {
  emit('update:modelValue', digits.value.join(''))
}

const replaceFrom = (startIndex, value) => {
  const nextDigits = [...digits.value]

  for (let index = startIndex; index < props.length; index += 1) {
    nextDigits[index] = ''
  }

  value
    .slice(0, props.length - startIndex)
    .split('')
    .forEach((digit, offset) => {
      nextDigits[startIndex + offset] = digit
    })

  digits.value = nextDigits
  emitValue()
}

const focusAfterValue = (startIndex, value) => {
  if (!value) {
    inputRefs[startIndex]?.focus()
    return
  }

  const targetIndex = Math.min(
    startIndex + value.length,
    props.length - 1,
  )

  inputRefs[targetIndex]?.focus()
}

const onInput = (index, event) => {
  const value = event.target.value.replace(/\D/g, '')

  if (value.length > 1) {
    replaceFrom(index, value)
    event.target.value = digits.value[index]
    focusAfterValue(index, value)
    return
  }

  const digit = value.slice(-1)
  digits.value[index] = digit
  event.target.value = digit
  emitValue()

  if (digit && index < props.length - 1) {
    inputRefs[index + 1]?.focus()
  }
}

const onKeydown = (index, event) => {
  if (event.key === 'Backspace' && !digits.value[index] && index > 0) {
    inputRefs[index - 1]?.focus()
    return
  }

  if (event.key === 'ArrowLeft' && index > 0) {
    event.preventDefault()
    inputRefs[index - 1]?.focus()
    return
  }

  if (event.key === 'ArrowRight' && index < props.length - 1) {
    event.preventDefault()
    inputRefs[index + 1]?.focus()
  }
}

const onPaste = (index, event) => {
  event.preventDefault()

  const pasted = event.clipboardData
    .getData('text')
    .replace(/\D/g, '')
    .slice(0, props.length - index)

  replaceFrom(index, pasted)
  focusAfterValue(index, pasted)
}
</script>

<template>
  <div
    class="auth-login__otp-wrap"
    :class="rootClasses"
  >
    <fieldset class="auth-login__otp-fieldset">
      <legend :class="legendClass">
        {{ legend }}
      </legend>
      <div
        class="auth-login__otp"
        role="group"
        :aria-describedby="error ? `${id}-error` : undefined"
      >
        <input
          v-for="(_, index) in digits"
          :id="index === 0 ? id : undefined"
          :key="index"
          :ref="(element) => setRef(element, index)"
          class="auth-login__otp-digit"
          :type="inputType"
          inputmode="numeric"
          pattern="[0-9]*"
          maxlength="1"
          :value="digits[index]"
          :autocomplete="index === 0 ? autocomplete : 'off'"
          :disabled="disabled"
          :aria-label="`${legend} digit ${index + 1} of ${length}`"
          @input="onInput(index, $event)"
          @keydown="onKeydown(index, $event)"
          @paste="onPaste(index, $event)"
        >
      </div>
    </fieldset>

    <button
      v-if="concealed && visibilityToggle"
      type="button"
      class="auth-login__otp-visibility"
      :disabled="disabled"
      :aria-pressed="isVisible ? 'true' : 'false'"
      :aria-label="isVisible ? 'Hide PIN' : 'Show PIN'"
      :title="isVisible ? 'Hide PIN' : 'Show PIN'"
      @click="isVisible = !isVisible"
    >
      <AuthIcon :name="isVisible ? 'eye-off' : 'eye'" />
      <span v-if="!compact && !profile">{{ isVisible ? 'Hide PIN' : 'Show PIN' }}</span>
    </button>

    <span
      v-if="error"
      :id="`${id}-error`"
      class="auth-login__error"
      role="alert"
    >
      {{ error }}
    </span>
  </div>
</template>

<style scoped>
.auth-login__otp-wrap--compact {
  max-width: 220px;
  margin-inline: auto;
  overflow: visible;
}

.auth-login__otp-wrap--with-visibility,
.auth-login__otp-wrap--profile {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 40px;
  align-items: center;
  column-gap: 8px;
}

.auth-login__otp-wrap--with-visibility {
  max-width: 268px;
}

.auth-login__otp-wrap--with-visibility .auth-login__otp-fieldset,
.auth-login__otp-wrap--profile .auth-login__otp-fieldset {
  grid-column: 1;
}

.auth-login__otp-wrap--with-visibility .auth-login__otp-visibility,
.auth-login__otp-wrap--profile .auth-login__otp-visibility {
  grid-column: 2;
  grid-row: 1;
}

.auth-login__otp-wrap--with-visibility .auth-login__error,
.auth-login__otp-wrap--profile .auth-login__error {
  grid-column: 1 / -1;
}

.auth-login__otp-wrap--compact .auth-login__otp {
  justify-content: center;
  gap: 8px;
  margin-bottom: 0;
}

.auth-login__otp-wrap--compact .auth-login__otp-digit {
  flex: 0 0 46px;
  width: 46px;
  height: 52px;
  aspect-ratio: auto;
  font-size: 20px;
}

.auth-login__otp-wrap--compact .auth-login__otp-visibility,
.auth-login__otp-wrap--profile .auth-login__otp-visibility {
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  border: 1px solid var(--auth-border, #cbd2d9);
  border-radius: 50%;
  background: var(--auth-input-bg, #fff);
}

.auth-login__otp-visibility {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 12px auto 0;
  padding: 6px 8px;
  border: 0;
  background: transparent;
  color: var(--auth-text, #1A1A1A);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.auth-login__otp-visibility:hover:not(:disabled) {
  color: var(--auth-accent, #1667a8);
}

.auth-login__otp-visibility:focus-visible {
  outline: 2px solid var(--auth-accent, #1667a8);
  outline-offset: 2px;
  border-radius: 6px;
}

.auth-login__otp-visibility:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-login__otp-wrap--profile {
  width: max-content;
}

.auth-login__otp-wrap--profile .auth-login__otp-visibility {
  align-self: end;
  margin-bottom: 3px;
}

.auth-login__otp-wrap--profile .auth-login__otp-fieldset {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.auth-login__otp-label--profile {
  margin-bottom: 8px;
  color: #1d2939;
  font-weight: 600;
}

.auth-login__otp-wrap--profile .auth-login__otp {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0;
}

.auth-login__otp-wrap--profile .auth-login__otp-digit {
  flex: 0 0 42px;
  width: 42px;
  height: 46px;
  padding: 0;
  border: 1px solid #cbd2d9;
  border-radius: 7px;
  background: #fff;
  color: #1d2939;
  font: inherit;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
}

.auth-login__otp-wrap--profile .auth-login__otp-digit:focus {
  border-color: #1667a8;
  outline: 2px solid rgba(22, 103, 168, 0.18);
  outline-offset: 1px;
}

.auth-login__otp-wrap--profile .auth-login__otp-digit:disabled {
  background: #f2f4f7;
  cursor: not-allowed;
}

@media (max-width: 480px) {
  .auth-login__otp-wrap--compact .auth-login__otp-digit {
    flex-basis: 42px;
    width: 42px;
    height: 48px;
  }

  .auth-login__otp-wrap--with-visibility {
    max-width: 244px;
  }

  .auth-login__otp-wrap--profile .auth-login__otp-digit {
    flex-basis: 38px;
    width: 38px;
    height: 44px;
  }

  .auth-login__otp-wrap--profile .auth-login__otp-visibility {
    margin-bottom: 2px;
  }
}
</style>
